import { Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Typography } from '@mui/material'
import { DynamicGridTypes } from 'types'

// Re-export from centralized styles for backwards compatibility
export {
  // Typography
  headerStyle,
  detailHeaderStyle,
  detailNameStyle,
  detailTextStyle,
  entityTextStyle as orgTextStyle,
  textStyle as textStyles,
  overlayTextStyle as overlayTextStyles,
  // Containers
  boxContainerStyles,
  alertBoxStyles as boxStyles,
  accountBoxStyles,
  gridContainerStyles,
  gridToolbarContainerStyles,
  buttonContainerStyles,
  popoverStyles,
  dialogContentWrapperStyles as dialogContentStyles,
  gridStyles,
  filterPanelProps,
  // TextFields
  FormTextField as OrganizationTextField,
  DealTextField as DealOnlyTextFields,
  CurrencyAdornment as StyledCurrencyAdornment,
  feeFieldStyles,
  totalsReadOnlyFieldStyles,
  // Buttons
  SaveButton,
  EditButton,
  CreateButton,
  HeaderButton,
  deleteButtonStyles,
  deleteIconStyles,
  // Info sections
  InfoBox,
  CustomInfoBox,
  InfoBoxContainer,
  InfoRow,
  InfoLabel,
  InfoValueWrapper,
  InfoValue,
  StyledLink,
  Underline,
  CityStateZipRow,
  DetailsCard as OrganizationsCard,
  DetailsBox as OrgDetailsBox,
  CompanyLabelChip,
} from 'styles/utilities'

// Deals-specific styled components
export const DualRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(2),
  position: 'relative',
}))

export const PhoneRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  marginBottom: theme.spacing(2),
  position: 'relative',
}))

/**
 * Dialog styles for Deals
 */
export const dialogStyles = (theme: { palette: { mode: string; background: any; text: any } }) => ({
  width: '100%',
  maxWidth: '800px',
  backgroundColor: theme.palette.mode === 'dark' ? '' : theme.palette.background.default,
  color: theme.palette.text.primary,
  minHeight: '50vh',
  maxHeight: '95vh',
  padding: '20px',
  '& .MuiDialogContent-root': {
    width: '100%',
    padding: '20px',
  },
  '& .MuiTextField-root': {
    margin: '10px 0',
    flex: '1 1 45%',
    minWidth: '150px',
  },
  '& .MuiInputBase-root': {
    backgroundColor: theme.palette.background.paper,
    minWidth: '150px',
    flex: '1 1 45%',
  },
  '& .MuiSelect-root': {
    backgroundColor: theme.palette.background.paper,
    flex: '1 1 45%',
    minWidth: '150px',
  },
})

/**
 * String to avatar initials helper
 */
export const stringAvatar = (name = '', fontsize?: number) => {
  const nameParts = name ? name.split(' ') : ['']

  if (nameParts.length < 2) {
    return {
      children: (
        <Typography
          sx={{
            fontSize: fontsize ? fontsize : 14,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%',
          }}
        >
          {nameParts[0][0] || ''}
        </Typography>
      ),
    }
  }

  return {
    children: (
      <Typography
        sx={{
          fontSize: fontsize ? fontsize : 14,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          height: '100%',
        }}
      >
        {`${nameParts[0][0]}${nameParts[1][0]}`}
      </Typography>
    ),
  }
}

/**
 * Dynamic grid sizes based on screen height
 */
export const dynamicGridSizes = (isTallScreen: boolean): DynamicGridTypes => ({
  contactRecipient: { xs: 12, sm: 12, md: 12, lg: 12, xl: 12 },
  authorizedRecipient: { xs: 12, sm: 12, md: 12, lg: 12, xl: 12 },
  dates: {
    xs: isTallScreen ? 12 : 6,
    sm: isTallScreen ? 12 : 6,
    md: isTallScreen ? 12 : 6,
    lg: isTallScreen ? 12 : 6,
    xl: isTallScreen ? 12 : 6,
  },
  numbers: {
    xs: isTallScreen ? 12 : 6,
    sm: isTallScreen ? 12 : 6,
    md: isTallScreen ? 12 : 6,
    lg: isTallScreen ? 12 : 6,
    xl: isTallScreen ? 12 : 6,
  },
  totalsContainer: { xs: 12, sm: 12, md: 12, lg: 12, xl: 12 },
  implementationFee: { xs: 12, sm: 12, md: 12, lg: 12, xl: 12 },
  noSignature: { xs: 6, sm: 12, md: 12, lg: 12, xl: 12 },
  numberOfPayments: { xs: 6, sm: 12, md: 12, lg: 12, xl: 12 },
  cancelButton: { xs: 2, sm: 2, md: 2, lg: 2, xl: 6 },
  saveButton: { xs: 2, sm: 2, md: 2, lg: 2, xl: 6 },
  requestButton: { xs: 12, sm: 12, md: 12, lg: 12, xl: 12 },
  generateButton: { xs: 6, sm: 6, md: 6, lg: 4, xl: 12 },
  requestContainer: { xs: 8, sm: 8, md: 8, lg: 8, xl: 12 },
})
